<template>
  <div class="notify-content-preview-block">
    <div v-if="loading" v-loading="loading" class="preview-loading" />
    <div v-if="!loading" class="chatbox">
      <div v-if="perviewError" class="flex justify-center">
        <p class="error-msg">提示：部分顯示欄位尚未設定資訊</p>
      </div>
      <div v-for="(message, idx) in lineMessagesData" :id="`message-${notifyIndex}-${idx}`" :key="`message-${notifyIndex}-${idx}`">
        <PreviewNoTemplateBlock v-if="get(message, 'content.NotifyContentTemplate.isRemove')" />
        <template v-else>
          <div
            v-for="(lineMessage, index) in message.lineMessages"
            :id="`message-${notifyIndex}-${idx}-${index}`"
            :key="`message-${notifyIndex}-${idx}-${index}`"
          >
            <PreviewTextBlock v-if="lineMessage.type === 'text'" :text="lineMessage.text" />
            <PreviewImageBlock v-if="lineMessage.type === 'image'" :url="lineMessage.previewImageUrl" />
          </div>
        </template>
        <PreviewHiddenBlock v-if="message.type === 'hidden'" />
      </div>
      <!-- 補充說明 -->
      <PreviewInfo />
    </div>
  </div>
</template>

<script>
import { defineComponent, onMounted, watch, ref, computed, nextTick } from 'vue'
import { PreviewProgressNotificationSettings } from '@/api/progressNotification'
// import { useFetch } from '@/use/fetch'
import { useDebounceFn } from '@vueuse/core'
import { useShop } from '@/use/shop'
import { flex2html } from '@/utils/flex2html'
import { passValue } from '@/utils/helper'
import { get, filter, map, includes } from 'lodash'
import PreviewInfo from './PreviewInfo.vue'
import PreviewTextBlock from './PreviewTextBlock.vue'
import PreviewImageBlock from './PreviewImageBlock.vue'
import PreviewHiddenBlock from './PreviewHiddenBlock.vue'
import PreviewNoTemplateBlock from './PreviewNoTemplateBlock.vue'
import { reservationNotificationSettingTypeConfigs } from '@/config/progressNotification'

export default defineComponent({
  name: 'NotifyContentPreviewBlock',
  components: {
    PreviewInfo,
    PreviewTextBlock,
    PreviewImageBlock,
    PreviewHiddenBlock,
    PreviewNoTemplateBlock,
  },
  props: {
    contents: { type: Array, default: () => [] },
    notifyIndex: { type: Number, default: 0 },
    notifyType: String,
    reservationConfig: { type: Object, default: () => ({}) },
  },
  setup (props) {
    // const { simpleFetch } = useFetch()
    const { shopId } = useShop()
    const loading = ref(false)
    const lineMessagesData = ref([])
    const perviewError = ref(false)
    // 與 ReservationInfoBlock 按鈕文字輸入框的 placeholder 一致
    const actionLabelPlaceholder = '請輸入按鈕文字'
    // 只保留最後一次請求的結果，避免快速切換開關時互相覆蓋
    let latestRequestSeq = 0
    watch(props, async () => {
      await getPreviewData()
    })

    const filterContent = computed(() => {
      const data = passValue(get(props, 'contents'))
      return filter(map(data, content => {
        if (content && content.appointmentInfoConfig) {
          content.appointmentInfoConfig.actions = map(content.appointmentInfoConfig.actions, (action) => ({
            ...action,
            linkUrl: action.linkUrl !== undefined && !action.linkUrl ? 'w' : action.linkUrl,
            // 按鈕已開啟但還沒填文字時，用佔位文字送預覽。
            // 後端 contents 的 Joi 規則在 enabled = true 時不接受空字串，
            // 直接送空值會讓整張預覽回 400 變成空白。
            label: action.enabled && !action.label
              ? actionLabelPlaceholder
              : (action.label !== undefined && !action.label ? '' : action.label),
          }))

          // 整合自動取消提示文字到 appointmentInfoConfig
          if (
            content.appointmentInfoConfig.showNoReplyAutoCancelHint &&
            props.reservationConfig.enableNoReplyAutoCancel &&
            props.reservationConfig.noReplyAutoCancelText
          ) {
            content.appointmentInfoConfig.noReplyAutoCancelText = props.reservationConfig.noReplyAutoCancelText
          }
        }
        return content
      }), content => content !== null)
    })

    const getPreviewData = useDebounceFn(async () => {
      // 不再因為前一個請求還在進行中就整個丟棄這次切換，
      // 改用序號讓最後一次切換的結果勝出
      latestRequestSeq += 1
      const requestSeq = latestRequestSeq
      const isStaleRequest = () => requestSeq !== latestRequestSeq
      loading.value = true

      // 如果有其中一個區塊沒有選擇範本 -> 不打 api
      const nullNotifyContentTemplateId = filter(get(props, 'contents'), (content) => {
        if (includes([
          reservationNotificationSettingTypeConfigs.appointmentCancel.value,
          reservationNotificationSettingTypeConfigs.appointmentEnd.value,
          reservationNotificationSettingTypeConfigs.appointmentStart.value,
        ], props.notifyType)) return false
        return get(content, 'NotifyContentTemplateId') === null
      })
      if (nullNotifyContentTemplateId.length > 0) {
        // 一定要清掉舊資料，否則畫面會留著上一個開關狀態的預覽，
        // 與提示文字並存造成「切開關顯示不一樣的東西」
        lineMessagesData.value = []
        perviewError.value = true
        loading.value = false
        return
      }
      // await simpleFetch(PreviewProgressNotificationSettings, {
      //   shopId: shopId.value,
      //   contents: filterContent.value,
      // }, async (res) => {
      //   lineMessagesData.value = res.result
      //   try {
      //     loading.value = false
      //     await nextTick()
      //     let count = 0
      //     for (const message of res.result) {
      //       if (message.type === 'lineMessages') {
      //         let lineCount = 0
      //         for (const lineMessage of message.lineMessages) {
      //           if (lineMessage.type === 'flex') flex2html(`message-${props.notifyIndex}-${count}-${lineCount}`, lineMessage)
      //           lineCount += 1
      //         }
      //       }
      //       count += 1
      //     }
      //   } catch (error) {
      //     console.log(error)
      //   }
      // }, false, false)
      const [res, err] = await PreviewProgressNotificationSettings({
        shopId: shopId.value,
        contents: filterContent.value,
        type: props.notifyType,
      })
      // 過期的回應不碰 loading：loading 由最新那次請求負責 settle，
      // 這裡搶著設 false 會在新請求還在飛的時候先顯示成「已完成」
      if (isStaleRequest()) return
      if (err) {
        console.error('[NotifyContentPreviewBlock] 預覽失敗', err)
        lineMessagesData.value = []
        perviewError.value = true
        loading.value = false
        return
      }
      try {
        lineMessagesData.value = res.result
        loading.value = false
        perviewError.value = false
        await nextTick()
        let count = 0
        for (const message of res.result) {
          if (message.type === 'lineMessages') {
            let lineCount = 0
            for (const lineMessage of message.lineMessages) {
              if (lineMessage.type === 'flex') flex2html(`message-${props.notifyIndex}-${count}-${lineCount}`, lineMessage)
              lineCount += 1
            }
          }
          count += 1
        }
      } catch (error) {
        console.log(error)
      }
    }, 300)

    onMounted(async () => {
      await getPreviewData()
    })
    return {
      getPreviewData,
      loading,
      lineMessagesData,
      get,
      perviewError,
    }
  },
})
</script>

<style lang="postcss" scoped>
@import './flex2html.css';

.notify-content-preview-block {
  @apply h-full w-full bg-[#849ebf];
}

.preview-loading {
  @apply flex-1 bg-[#849ebf] w-full h-full;
}

.alert-area {
  @apply bg-white w-[300px] p-[20px] m-auto rounded-[17px] mb-[20px];
}
.error-msg {
  @apply text-sm text-white bg-[#00000080] rounded-[8px] px-[8px] py-[3.5px] mb-[12px]
}
</style>
